/**
 * DEFCON TERMINAL - System Discovery Display
 *
 * Handles the display and selection interface for discovered systems
 * after war dialer scanning is complete.
 *
 * @module SystemDiscovery
 */

import type { PathType } from '@shared/types';
import { SYSTEM_NAMES, ASCII_DIMENSIONS } from '@shared/constants';
import type { DiscoveredSystem } from './PhoneGenerator';

/**
 * System selection callback
 */
export type SystemSelectCallback = (system: DiscoveredSystem) => void;

/**
 * System discovery display state
 */
export interface DiscoveryState {
  systems: DiscoveredSystem[];
  selectedIndex: number;
  revealed: boolean[];
  isSelecting: boolean;
}

/**
 * System description with classification level
 */
interface SystemClassification {
  classification: string;
  agency: string;
  threatLevel: string;
  accessLevel: string;
}

/**
 * Classification data for each system type
 */
const SYSTEM_CLASSIFICATIONS: Record<PathType, SystemClassification> = {
  'sarn-west': {
    classification: 'TOP SECRET//SCI//NOFORN',
    agency: 'DEPARTMENT OF DEFENSE',
    threatLevel: 'CRITICAL',
    accessLevel: 'LEVEL 5 CLEARANCE REQUIRED',
  },
  'sarn-east': {
    classification: '\u0421\u0415\u041A\u0420\u0415\u0422\u041D\u041E // TOP SECRET',
    agency: 'MINISTRY OF DEFENSE USSR',
    threatLevel: '\u041A\u0420\u0418\u0422\u0418\u0427\u0415\u0421\u041A\u0418\u0419',
    accessLevel: 'GENERAL STAFF ONLY',
  },
  'phantom-directive': {
    classification: 'TOP SECRET//SAP//ORCON',
    agency: 'CENTRAL INTELLIGENCE AGENCY',
    threatLevel: 'BLACK',
    accessLevel: 'SPECIAL ACCESS PROGRAM',
  },
  'red-shadow': {
    classification: '\u0421\u041E\u0412\u0415\u0420\u0428\u0415\u041D\u041D\u041E \u0421\u0415\u041A\u0420\u0415\u0422\u041D\u041E',
    agency: 'KGB - DIRECTORATE S',
    threatLevel: '\u041E\u041F\u0415\u0420\u0410\u0422\u0418\u0412\u041D\u042B\u0419',
    accessLevel: 'ILLEGALS PROGRAM',
  },
};

/**
 * System discovery interface manager
 */
export class SystemDiscovery {
  private state: DiscoveryState;
  private onSelect: SystemSelectCallback | null = null;

  constructor() {
    this.state = {
      systems: [],
      selectedIndex: 0,
      revealed: [],
      isSelecting: false,
    };
  }

  /**
   * Initialize with discovered systems
   */
  initialize(systems: DiscoveredSystem[]): void {
    this.state.systems = systems;
    this.state.selectedIndex = 0;
    this.state.revealed = systems.map(() => false);
    this.state.isSelecting = true;
  }

  /**
   * Get current state
   */
  getState(): DiscoveryState {
    return { ...this.state };
  }

  /**
   * Set selection callback
   */
  setOnSelect(callback: SystemSelectCallback): void {
    this.onSelect = callback;
  }

  /**
   * Move selection up
   */
  selectPrevious(): void {
    if (this.state.systems.length === 0) return;
    this.state.selectedIndex = (this.state.selectedIndex - 1 + this.state.systems.length) % this.state.systems.length;
  }

  /**
   * Move selection down
   */
  selectNext(): void {
    if (this.state.systems.length === 0) return;
    this.state.selectedIndex = (this.state.selectedIndex + 1) % this.state.systems.length;
  }

  /**
   * Select by index
   */
  selectByIndex(index: number): void {
    if (index >= 0 && index < this.state.systems.length) {
      this.state.selectedIndex = index;
    }
  }

  /**
   * Reveal system details
   */
  revealSystem(index: number): void {
    if (index >= 0 && index < this.state.revealed.length) {
      this.state.revealed[index] = true;
      if (this.state.systems[index]) {
        this.state.systems[index].discovered = true;
      }
    }
  }

  /**
   * Reveal all systems
   */
  revealAll(): void {
    this.state.revealed = this.state.revealed.map(() => true);
    this.state.systems.forEach(s => s.discovered = true);
  }

  /**
   * Confirm selection and trigger callback
   */
  confirmSelection(): DiscoveredSystem | null {
    const selectedSystem = this.state.systems[this.state.selectedIndex];
    if (selectedSystem && this.onSelect) {
      this.state.isSelecting = false;
      this.onSelect(selectedSystem);
    }
    return selectedSystem;
  }

  /**
   * Get currently selected system
   */
  getSelectedSystem(): DiscoveredSystem | null {
    return this.state.systems[this.state.selectedIndex] || null;
  }

  /**
   * Generate system list display
   */
  generateSystemList(): string[] {
    const width = ASCII_DIMENSIONS.terminalWidth;
    const hr = '\u2550'.repeat(width - 2);
    const thinHr = '\u2500'.repeat(width - 4);

    const lines: string[] = [
      `\u2554${hr}\u2557`,
      `\u2551${this.centerText('DISCOVERED SYSTEMS', width - 2)}\u2551`,
      `\u2551${this.centerText('SELECT TARGET FOR CONNECTION', width - 2)}\u2551`,
      `\u2560${hr}\u2563`,
      `\u2551${this.padRight('', width - 2)}\u2551`,
    ];

    // Add each system
    this.state.systems.forEach((system, index) => {
      const isSelected = index === this.state.selectedIndex;
      const isRevealed = this.state.revealed[index];
      const prefix = isSelected ? '>> ' : '   ';
      const suffix = isSelected ? ' <<' : '   ';

      const systemName = isRevealed ? system.systemName : system.obfuscatedName;
      const phoneDisplay = system.phoneNumber;

      lines.push(`\u2551${this.padRight(`${prefix}[${index + 1}] ${phoneDisplay} - ${systemName}${suffix}`, width - 2)}\u2551`);

      if (isSelected) {
        // Show details for selected system
        lines.push(`\u2551${this.padRight('', width - 2)}\u2551`);
        lines.push(`\u2551  ${thinHr}  \u2551`);

        const classification = SYSTEM_CLASSIFICATIONS[system.systemType];
        const description = isRevealed ? system.description : system.obfuscatedDescription;

        lines.push(`\u2551${this.padRight(`      CLASSIFICATION: ${classification.classification}`, width - 2)}\u2551`);
        lines.push(`\u2551${this.padRight(`      AGENCY: ${classification.agency}`, width - 2)}\u2551`);
        lines.push(`\u2551${this.padRight(`      DESCRIPTION: ${description}`, width - 2)}\u2551`);
        lines.push(`\u2551${this.padRight(`      BAUD RATE: ${system.connectionBaudRate}`, width - 2)}\u2551`);
        lines.push(`\u2551${this.padRight(`      THREAT LEVEL: ${classification.threatLevel}`, width - 2)}\u2551`);
        lines.push(`\u2551  ${thinHr}  \u2551`);
      }

      lines.push(`\u2551${this.padRight('', width - 2)}\u2551`);
    });

    // Footer with controls
    lines.push(`\u2560${hr}\u2563`);
    lines.push(`\u2551${this.centerText('[W/UP] Previous  [S/DOWN] Next  [ENTER] Connect  [R] Reveal', width - 2)}\u2551`);
    lines.push(`\u255A${hr}\u255D`);

    return lines;
  }

  /**
   * Generate compact system summary (for embedded display)
   */
  generateCompactList(): string[] {
    const lines: string[] = [
      '  DISCOVERED SYSTEMS:',
      '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
    ];

    this.state.systems.forEach((system, index) => {
      const isSelected = index === this.state.selectedIndex;
      const isRevealed = this.state.revealed[index];
      const marker = isSelected ? '>>' : '  ';
      const name = isRevealed ? system.systemName : system.obfuscatedName;
      lines.push(`  ${marker} [${index + 1}] ${system.phoneNumber} - ${name}`);
    });

    return lines;
  }

  /**
   * Generate single system detail card
   */
  generateSystemCard(system: DiscoveredSystem, revealed: boolean = false): string[] {
    const width = 60;
    const hr = '\u2500'.repeat(width - 2);
    const classification = SYSTEM_CLASSIFICATIONS[system.systemType];

    const systemName = revealed ? system.systemName : system.obfuscatedName;
    const description = revealed ? system.description : system.obfuscatedDescription;

    return [
      `\u250C${hr}\u2510`,
      `\u2502${this.centerText('SYSTEM PROFILE', width - 2)}\u2502`,
      `\u251C${hr}\u2524`,
      `\u2502${this.padRight(`  PHONE: ${system.phoneNumber}`, width - 2)}\u2502`,
      `\u2502${this.padRight(`  NAME: ${systemName}`, width - 2)}\u2502`,
      `\u2502${this.padRight(`  DESC: ${description}`, width - 2)}\u2502`,
      `\u2502${this.padRight(`  BAUD: ${system.connectionBaudRate}`, width - 2)}\u2502`,
      `\u251C${hr}\u2524`,
      `\u2502${this.padRight(`  CLASS: ${classification.classification}`, width - 2)}\u2502`,
      `\u2502${this.padRight(`  AGENCY: ${classification.agency}`, width - 2)}\u2502`,
      `\u2502${this.padRight(`  ACCESS: ${classification.accessLevel}`, width - 2)}\u2502`,
      `\u2514${hr}\u2518`,
    ];
  }

  /**
   * Generate warning display for system connection
   */
  generateConnectionWarning(system: DiscoveredSystem): string[] {
    const systemConfig = SYSTEM_NAMES[system.systemType];
    const displayName = 'englishName' in systemConfig && systemConfig.englishName
      ? systemConfig.englishName
      : systemConfig.name;

    return [
      '',
      '\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588',
      '\u2588                                                \u2588',
      '\u2588              W A R N I N G                      \u2588',
      '\u2588                                                \u2588',
      '\u2588  UNAUTHORIZED ACCESS TO THIS SYSTEM IS A       \u2588',
      '\u2588  FEDERAL CRIME PUNISHABLE BY IMPRISONMENT      \u2588',
      '\u2588  AND/OR FINES UNDER 18 U.S.C. 1030              \u2588',
      '\u2588                                                \u2588',
      `\u2588  TARGET: ${displayName}`.padEnd(49) + '\u2588',
      '\u2588                                                \u2588',
      '\u2588  PROCEED WITH CONNECTION? [Y/N]                \u2588',
      '\u2588                                                \u2588',
      '\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588',
      '',
    ];
  }

  /**
   * Helper: Center text within width
   */
  private centerText(text: string, width: number): string {
    const padding = Math.max(0, width - text.length);
    const leftPad = Math.floor(padding / 2);
    const rightPad = padding - leftPad;
    return ' '.repeat(leftPad) + text + ' '.repeat(rightPad);
  }

  /**
   * Helper: Pad text to fill width
   */
  private padRight(text: string, width: number): string {
    return text + ' '.repeat(Math.max(0, width - text.length));
  }
}

export default SystemDiscovery;
